import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Users, Loader2, AlertCircle, Image as ImageIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { obtenerMascotasPerdidas, Mascota, ObtenerMascotasPerdidasRequest } from "../../../../api/mascotaService";
import Swal from "sweetalert2";
import LostPetDetailModal from "./LostPetDetailModal";

export default function LostPetsPage() {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  
  const [mascotas, setMascotas] = useState<Mascota[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMascota, setSelectedMascota] = useState<Mascota | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const getImageUrl = (rutaFoto?: string) => {
    if (!rutaFoto || rutaFoto === "no_hay_imagen.png") {
      return null;
    }
    const fotoString = String(rutaFoto).trim();
    if (!fotoString || fotoString === "") {
      return null;
    }
    if (fotoString.startsWith('http://') || fotoString.startsWith('https://')) {
      return fotoString;
    }
    if (fotoString.includes('img.easyph.com.co')) {
      return fotoString.startsWith('http') ? fotoString : `https://${fotoString}`;
    }
    const apiBaseUrl = process.env.REACT_APP_API_PROD || 'https://api.easyph.com.co/api/';
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${fotoString.replace(/^\//, '')}`;
  };

  // Función para cargar las mascotas perdidas
  const fetchMascotas = async () => {
    if (!unidadId) {
      setError("No se encontró el código de la unidad residencial.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload: ObtenerMascotasPerdidasRequest = {
        unidad_residencial_codigo: unidadId,
        torre_codigo: "",
        apartamento_codigo: "",
        mascota_codigo: "",
        esta_perdida: "1"
      };
      
      const response = await obtenerMascotasPerdidas(payload);
      
      if (response.code === "1" && response.object_) {
        setMascotas(response.object_);
        
        if (response.object_.length === 0) {
          Swal.fire({
            icon: "info",
            title: "Sin datos",
            text: "No se encontraron mascotas perdidas.",
            confirmButtonColor: "#1a3c34",
          });
        }
      } else {
        setMascotas([]);
      }
    } catch (err) {
      const errorMessage = "Error al cargar las mascotas perdidas: " + (err as Error).message;
      setError(errorMessage);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar mascotas al montar
  useEffect(() => {
    fetchMascotas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadId]);

  const handleSelectMascota = (mascota: Mascota) => {
    setSelectedMascota(mascota);
    setShowDetailModal(true);
  };

  const handleCloseModal = (success?: boolean) => {
    setShowDetailModal(false);
    setSelectedMascota(null);
    if (success) {
      // Recargar la lista si se reportó como encontrada
      fetchMascotas();
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900 w-full overflow-x-hidden">
      <Sidebar />
      
      <main className="flex-1 p-3 sm:p-4 md:p-6 md:ml-64 transition-all w-full max-w-full overflow-x-hidden">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 md:mb-6 text-military-600 break-words">
          Mascotas Perdidas
        </h2>

        <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white w-full">
          <CardHeader className="p-3 sm:p-4 md:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <CardTitle className="text-base sm:text-lg md:text-xl text-military-600 flex items-center gap-2 w-full sm:w-auto">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">Lista de Mascotas Perdidas</span>
              </CardTitle>
              <Button
                onClick={fetchMascotas}
                variant="outline"
                className="w-full sm:w-auto text-sm sm:text-base px-3 sm:px-4 py-2"
                disabled={isLoading}
              >
                Actualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0 sm:pt-0 md:pt-0">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-military-500" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            ) : mascotas.length === 0 ? (
              <p className="text-gray-600 text-center py-8 text-sm sm:text-base">
                No hay mascotas perdidas reportadas.
              </p>
            ) : (
              <>
                {/* Vista de Cards para móvil */}
                <div className="block md:hidden space-y-3 sm:space-y-4">
                  {mascotas.map((mascota) => {
                    const foto = mascota.mas_foto_nombre || mascota.mas_foto_ruta || (mascota as any).mas_foto || (mascota as any).mas_ruta_foto || (mascota as any).foto;
                    const imageUrl = getImageUrl(foto);
                    
                    return (
                      <div
                        key={mascota.mas_codigo}
                        onClick={() => handleSelectMascota(mascota)}
                        className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md transition cursor-pointer w-full"
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="flex-shrink-0">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={mascota.mas_nombre || "Mascota"}
                                className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover border-2 border-military-500"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  if (target.nextElementSibling) {
                                    (target.nextElementSibling as HTMLElement).style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-military-500 ${imageUrl ? 'hidden' : ''}`}
                            >
                              <ImageIcon className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base sm:text-lg text-gray-900 break-words">
                              {mascota.mas_nombre || "Sin nombre"}
                            </h3>
                            <div className="mt-2 space-y-1 text-xs sm:text-sm text-gray-600">
                              {mascota.mas_tipo_mascota_nombre && (
                                <p>Tipo: {mascota.mas_tipo_mascota_nombre}</p>
                              )}
                              {mascota.mas_raza && (
                                <p>Raza: {mascota.mas_raza}</p>
                              )}
                              {mascota.mas_torre_nombre && mascota.mas_apartamento_nombre && (
                                <p>Torre {mascota.mas_torre_nombre} - Apt {mascota.mas_apartamento_nombre}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Vista de Tabla para desktop */}
                <div className="hidden md:block overflow-x-auto w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Foto</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Nombre</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Tipo</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Raza</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Ubicación</th>
                              <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {mascotas.map((mascota) => {
                              const foto = mascota.mas_foto_nombre || mascota.mas_foto_ruta || (mascota as any).mas_foto || (mascota as any).mas_ruta_foto || (mascota as any).foto;
                              const imageUrl = getImageUrl(foto);
                              
                              return (
                                <tr
                                  key={mascota.mas_codigo}
                                  className="hover:bg-gray-50 transition"
                                >
                                  <td className="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={mascota.mas_nombre}
                                        className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover border-2 border-military-500"
                                      />
                                    ) : (
                                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-military-500">
                                        <ImageIcon className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3">
                                    <div className="text-xs md:text-sm font-medium text-gray-900 break-words max-w-xs">
                                      {mascota.mas_nombre || "Sin nombre"}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words">
                                    {mascota.mas_tipo_mascota_nombre || "No especificado"}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words">
                                    {mascota.mas_raza || "No especificada"}
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3">
                                    <div className="text-xs md:text-sm text-gray-900 break-words">
                                      {mascota.mas_torre_nombre || "N/A"}
                                    </div>
                                    <div className="text-xs md:text-sm text-gray-500 break-words">
                                      {mascota.mas_apartamento_nombre || ""}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm font-medium">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleSelectMascota(mascota)}
                                      className="border-military-500 text-military-500 hover:bg-military-100 text-xs md:text-sm px-2 md:px-3 py-1 md:py-2"
                                    >
                                      Ver Detalle
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Modal de Detalle */}
        {selectedMascota && (
          <LostPetDetailModal
            isOpen={showDetailModal}
            mascota={selectedMascota}
            onClose={handleCloseModal}
          />
        )}
      </main>
    </div>
  );
}

